import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStoreFeature,
  type,
  withMethods,
  withState,
  type SignalStoreFeature,
} from '@ngrx/signals';
import {
  entityConfig,
  setAllEntities,
  withEntities,
} from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap } from 'rxjs';
import { Country, CountryParams, State, StateParams } from '../../../types';
import { fetchCountries, fetchStates } from '../../utils';
import { Injector } from '@angular/core';

/**
 * Entity Config allows creating an entity store object.
 * {entity: type(DATA_STRUCTURE), collection: string, selectId:(entity) => entity.id}
 * selectId is a function that returns a unique id for each entity collection.
 * */
const countryEntity = () =>
  entityConfig({
    collection: '_countries',
    entity: type<Country>(),
    selectId: (entity) => entity.id,
  });

const stateEntity = () =>
  entityConfig({
    collection: '_states',
    entity: type<State>(),
    selectId: (entity) => entity.id,
  });

/**
 * withCountryEntities is a function that creates a signal store feature
 * for managing the country entity slice. It provides an ability to plug and play
 * It uses the NgRx Signals library to create a reactive store feature.
 */
export const withCountryEntities: (
  envUrl: string,
  _injector: Injector,
) => SignalStoreFeature = (envUrl: string, _injector: Injector) => {
  return signalStoreFeature(
    withState(() => ({ countriesTotal: 0, statesTotal: 0 })),
    withEntities(countryEntity()),
    withEntities(stateEntity()),
    withMethods((store) => {
      const _fetchCountries = rxMethod<CountryParams>(
        pipe(
          switchMap((params) =>
            fetchCountries(
              envUrl,
              params.pageIndex,
              params.pageSize,
              _injector,
            ).pipe(
              tapResponse({
                error: (error: Error) =>
                  console.error('Error loading countries:', error),
                next: (response) => {
                  patchState(
                    store,
                    { countriesTotal: response.total },
                    setAllEntities(response.data, countryEntity()),
                  );
                },
              }),
            ),
          ),
        ),
      );
      const _fetchStates = rxMethod<StateParams>(
        pipe(
          switchMap((params) =>
            fetchStates(envUrl, _injector, params.countryId).pipe(
              tapResponse({
                error: (error: Error) =>
                  console.error('Error loading states:', error),
                next: (response) => {
                  patchState(
                    store,
                    { statesTotal: response.total },
                    setAllEntities(response.data, stateEntity()),
                  );
                },
              }),
            ),
          ),
        ),
      );
      return {
        setCountries: (params: CountryParams) => _fetchCountries(params),
        setStates: (params: StateParams) => _fetchStates(params),
      };
    }),
  );
};
